// Pure geometry for the image Crop tool - takes the crop rectangle the user dragged on screen
// (which always shares the image's own angle, see design-3d.component.ts's startImageCrop()) and
// resolves it against the image's current transform, in both directions:
//   - resolveCropRect(): what the user is currently dragging -> a crop clamped to stay within the
//     image's own bounds, for both live redraw and the final Apply.
//   - cropResultFor(): the clamped rect -> the FabricImage properties (cropX/cropY/width/height/
//     left/top) that actually perform the crop.
//
// The image's own scaleX/scaleY/angle are left untouched by a crop - only cropX/cropY/width/height
// (which slice its source pixels) and left/top (to keep the visible box centered where the crop
// rectangle was) change. That's what makes a crop reversible/re-croppable without any quality loss:
// the full source image stays decoded, only the visible window into it moves.

export interface ImageCropTransform {
  left: number;
  top: number;
  angle: number;
  scaleX: number;
  scaleY: number;
  width: number;
  height: number;
  cropX: number;
  cropY: number;
}

export interface CropRectTransform {
  left: number;
  top: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
}

export interface ResolvedCropRect {
  // Local, unscaled offset of the crop rect's center from the image's own center, in the image's
  // (unrotated) logical coordinate space - i.e. already divided by the image's scaleX/scaleY.
  offsetX: number;
  offsetY: number;
  // Logical (unscaled) size of the crop rect, in that same space.
  width: number;
  height: number;
}

export interface CropResult {
  cropX: number;
  cropY: number;
  width: number;
  height: number;
  left: number;
  top: number;
}

const MIN_CROP_SIZE = 1;

// Converts a crop rectangle (assumed to share the image's angle - see the file comment) from
// canvas space into the image's own local logical frame, then clamps it to the image's current
// bounds - a rect can never grow past, or move outside of, the box the image is currently showing,
// since anything within that box is guaranteed to map to valid source pixels.
export function resolveCropRect(image: ImageCropTransform, rect: CropRectTransform): ResolvedCropRect {
  const rad = (image.angle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = rect.left - image.left;
  const dy = rect.top - image.top;
  // Un-rotate the canvas-space offset into the image's own (unrotated) axes.
  const localX = dx * cos + dy * sin;
  const localY = -dx * sin + dy * cos;

  const rectDisplayWidth = rect.width * rect.scaleX;
  const rectDisplayHeight = rect.height * rect.scaleY;

  let width = Math.min(rectDisplayWidth / image.scaleX, image.width);
  let height = Math.min(rectDisplayHeight / image.scaleY, image.height);
  width = Math.max(width, MIN_CROP_SIZE);
  height = Math.max(height, MIN_CROP_SIZE);

  let offsetX = localX / image.scaleX;
  let offsetY = localY / image.scaleY;

  // Clamp so [offset - size/2, offset + size/2] stays inside [-image.width/2, image.width/2].
  const maxOffsetX = image.width / 2 - width / 2;
  const maxOffsetY = image.height / 2 - height / 2;
  offsetX = Math.min(Math.max(offsetX, -maxOffsetX), maxOffsetX);
  offsetY = Math.min(Math.max(offsetY, -maxOffsetY), maxOffsetY);

  return { offsetX, offsetY, width, height };
}

// Turns an already-resolved (clamped) crop rect back into the FabricImage properties that apply
// it - cropX/cropY fold in the image's own existing crop offset, so re-cropping an already-cropped
// image narrows the current crop instead of resetting to the full original source.
export function cropResultFor(image: ImageCropTransform, resolved: ResolvedCropRect): CropResult {
  const cropX = image.cropX + image.width / 2 + resolved.offsetX - resolved.width / 2;
  const cropY = image.cropY + image.height / 2 + resolved.offsetY - resolved.height / 2;
  const rad = (image.angle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Rotate the resolved local offset back into canvas space to get the new center position.
  const canvasDx = resolved.offsetX * image.scaleX * cos - resolved.offsetY * image.scaleY * sin;
  const canvasDy = resolved.offsetX * image.scaleX * sin + resolved.offsetY * image.scaleY * cos;
  return {
    cropX,
    cropY,
    width: resolved.width,
    height: resolved.height,
    left: image.left + canvasDx,
    top: image.top + canvasDy,
  };
}

// Canvas-space geometry for the crop-rect overlay itself, so a clamped resolve can snap the
// on-screen handles back within bounds mid-drag (not just at Apply time).
export function cropRectTransformFor(image: ImageCropTransform, resolved: ResolvedCropRect): CropRectTransform {
  const result = cropResultFor(image, resolved);
  return {
    left: result.left,
    top: result.top,
    width: resolved.width,
    height: resolved.height,
    scaleX: image.scaleX,
    scaleY: image.scaleY,
  };
}

interface RectLike {
  left: number;
  top: number;
  angle: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
}

// The 4 corners of a center-origin, rotated rect, in canvas space - used to build the dim mask's
// evenodd path below (a plain clipPath couldn't do this: see startImageCrop()'s comment on why an
// inverted, absolutely-positioned, rotated clipPath doesn't render as expected in Fabric).
function rotatedRectCorners(rect: RectLike): { x: number; y: number }[] {
  const halfWidth = (rect.width * rect.scaleX) / 2;
  const halfHeight = (rect.height * rect.scaleY) / 2;
  const rad = (rect.angle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return [
    [-halfWidth, -halfHeight],
    [halfWidth, -halfHeight],
    [halfWidth, halfHeight],
    [-halfWidth, halfHeight],
  ].map(([localX, localY]) => ({
    x: rect.left + localX * cos - localY * sin,
    y: rect.top + localX * sin + localY * cos,
  }));
}

// An evenodd-filled "frame" path - opaque everywhere inside outer's bounds except inside inner's
// (the crop rect) - built fresh from absolute canvas coordinates, so it needs no clipPath, no
// caching quirks, and no absolutePositioned/inverted combination to get right.
export function dimMaskPathData(outer: RectLike, inner: RectLike): string {
  const ring = (points: { x: number; y: number }[]): string =>
    `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y} L ${points[2].x} ${points[2].y} L ${points[3].x} ${points[3].y} Z`;
  return `${ring(rotatedRectCorners(outer))} ${ring(rotatedRectCorners(inner))}`;
}
